import { reactive } from 'vue'

export interface AppointmentForm {
  name: string
  name_th: string
  name_en: string
  mobile: string
  email: string
  branch: string | null
  doctor: string | null
  date: string
  time: any[]
  note: string
  allergy: string
  title: string
  sex: string
  birthdate: string
}

export interface FormDisableState {
  disableName: boolean
  disableMobile: boolean
  disableEmail: boolean
}

/**
 * Create note using for submission
 * note will look like:
 * หมายเหตุ: {note}
 *
 * {depositTypesName1, depositTypesName2, ...} : {interestTagsName1, interestTagsName2, ...}
 */
export function createSubmitNote(note: string, depositTypesName: string[], interestTagsName: string[]): string {
  let submitNote = note

  if (depositTypesName.length > 0) {
    submitNote += `\n${depositTypesName.join(', ')}`
  }

  if (interestTagsName.length > 0) {
    submitNote += ` : ${interestTagsName.join(', ')}`
  }

  return submitNote
}

export function useAppointmentForm() {
  const form = reactive<AppointmentForm>({
    name: '',
    name_th: '',
    name_en: '',
    mobile: '',
    email: '',
    branch: null,
    doctor: null,
    date: '',
    time: [],
    note: '',
    allergy: '',
    title: '',
    sex: '',
    birthdate: '',
  })

  const disableState = reactive<FormDisableState>({
    disableName: false,
    disableMobile: false,
    disableEmail: false,
  })

  /**
   * Validate mobile phone number (Thai format)
   */
  function mobileValidator(val: string): boolean {
    return /^0\d{9}$/.test(val)
  }

  /**
   * Validate email format
   */
  function emailValidator(string: string): boolean {
    if (string) {
      return /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(string)
    }
    return true
  }

  /**
   * Populate form from patient data
   */
  function populateFormFromPatient(patientData: any, language: string) {
    if (patientData) {
      form.name = language === 'th' || !patientData.fname_en ? patientData.fname : patientData.fname_en
      form.name_th = patientData.fname
      form.name_en = patientData.fname_en
      form.mobile = patientData.mobile
      form.email = patientData.email
      disableState.disableName = true
      disableState.disableMobile = true
      disableState.disableEmail = patientData.email ? true : false
    }
  }

  /**
   * Update name based on language change
   */
  function updateNameByLanguage(language: string) {
    if (form.name_en && language === 'en') {
      form.name = form.name_en
    } else {
      form.name = form.name_th
    }
  }

  /**
   * Reset form to initial state
   */
  function resetForm() {
    form.name = ''
    form.name_th = ''
    form.name_en = ''
    form.mobile = ''
    form.email = ''
    form.branch = null
    form.doctor = null
    form.date = ''
    form.time = []
    form.note = ''
    form.allergy = ''
    form.title = ''
    form.sex = ''
    form.birthdate = ''
    disableState.disableName = false
    disableState.disableMobile = false
    disableState.disableEmail = false
  }

  function resetFormKeepPatientData() {
    form.branch = null
    form.doctor = null
    form.date = ''
    form.time = []
    form.note = ''
    form.allergy = ''
    form.title = ''
    form.sex = ''
    form.birthdate = ''
    disableState.disableName = false
    disableState.disableMobile = false
    disableState.disableEmail = false
  }

  return {
    form,
    disableState,
    mobileValidator,
    emailValidator,
    populateFormFromPatient,
    updateNameByLanguage,
    resetForm,
    resetFormKeepPatientData,
    createSubmitNote,
  }
}
